import { useEffect, useRef, useState } from 'react';
import './platform-release.css';
import { platformRoutes } from './platform-routes';
import { currentPortal, portalLabels } from './portal-context';

interface ReleaseInfo {
  readonly id: string;
  readonly source: string;
  readonly publishedAt: string;
}

function releaseFrom(value: unknown): ReleaseInfo | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  return typeof record.id === 'string' &&
    typeof record.source === 'string' &&
    typeof record.publishedAt === 'string'
    ? { id: record.id, source: record.source, publishedAt: record.publishedAt }
    : null;
}

/** Optional local-host metadata. No business data or automatic page reload. */
export function PlatformReleaseStatus() {
  const portal = currentPortal();
  const bar = useRef<HTMLElement>(null);
  const [loaded] = useState(() =>
    typeof document === 'undefined'
      ? null
      : releaseFrom(
          JSON.parse(document.getElementById('platform-release')?.textContent ?? 'null') as unknown,
        ),
  );
  const [latest, setLatest] = useState<ReleaseInfo | null>(null);
  const [services, setServices] = useState('正在检查数据服务');
  const [disconnected, setDisconnected] = useState(false);
  useEffect(() => {
    if (!bar.current) return;
    const element = bar.current;
    const measure = () =>
      document.documentElement.style.setProperty(
        '--platform-release-height',
        `${element.getBoundingClientRect().height}px`,
      );
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--platform-release-height');
    };
  }, [loaded]);
  useEffect(() => {
    if (!loaded) return;
    let disposed = false;
    const controller = new AbortController();
    async function check() {
      try {
        const response = await fetch('/__platform/status', {
          cache: 'no-store',
          signal: controller.signal,
        });
        if (!response.ok) throw new Error('Platform unavailable');
        const value = (await response.json()) as {
          release?: unknown;
          services?: { monitoring?: string; reads?: string; management?: string };
        };
        if (disposed) return;
        setLatest(releaseFrom(value.release));
        setDisconnected(false);
        const label = (state: string | undefined) =>
          state === 'reachable' ? '已连接' : state === 'unavailable' ? '不可用' : '未连接';
        setServices(
          portal === 'vendor' || portal === 'admin'
            ? `业务服务：${label(value.services?.management)}`
            : `监测服务：${label(value.services?.monitoring)} · 能力与任务读取：${label(value.services?.reads)} · 业务服务：${label(value.services?.management)}`,
        );
      } catch {
        if (!disposed) setDisconnected(true);
      }
    }
    void check();
    const timer = window.setInterval(() => void check(), 30000);
    window.addEventListener('focus', check);
    return () => {
      disposed = true;
      controller.abort();
      window.clearInterval(timer);
      window.removeEventListener('focus', check);
    };
  }, [loaded, portal]);
  if (!loaded) return null;
  return (
    <aside ref={bar} className="platform-release" aria-label="平台版本与连接状态">
      <a href="/">{portal ? portalLabels[portal] : '主平台'}</a>
      <span title={`发布时间：${loaded.publishedAt} · ${loaded.id}`}>
        本地集成 · {loaded.id.split('-').at(-1)} · {loaded.source.slice(0, 8)}
      </span>
      <span role="status">{disconnected ? '平台服务连接中断，请检查启动状态' : services}</span>
      {(!portal || portal === 'firefighter') && <a href={platformRoutes.queries}>开发查询</a>}
      {latest && latest.id !== loaded.id && (
        <span role="status">
          有新版本，请保存当前内容后
          <button type="button" onClick={() => window.location.reload()}>
            刷新页面
          </button>
        </span>
      )}
    </aside>
  );
}
